import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'

interface Profile {
  url: string
  name: string
  title: string
  company: string
  intent_match?: number
  baseline_score?: number
  provider_id?: string
}

const columnHelper = createColumnHelper<Profile>()

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('title', {
    header: 'Title',
    cell: (info) => (
      <span className="text-sm text-slate-400 max-w-xs truncate block">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('company', {
    header: 'Company',
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),
  columnHelper.accessor('intent_match', {
    header: 'Intent',
    cell: (info) => {
      const score = info.getValue()
      if (score == null) return <span className="text-xs text-slate-500">-</span>
      const percentage = Math.round(score * 100)
      const color = percentage >= 70 ? 'text-green-400' : percentage >= 50 ? 'text-yellow-400' : 'text-red-400'
      return <span className={`text-xs font-mono font-medium ${color}`}>{percentage}%</span>
    },
  }),
  columnHelper.accessor('baseline_score', {
    header: 'Baseline',
    cell: (info) => {
      const score = info.getValue()
      if (score == null) return <span className="text-xs text-slate-500">-</span>
      const color = score >= 40 ? 'text-green-400' : score >= 20 ? 'text-yellow-400' : 'text-red-400'
      return <span className={`text-xs font-mono font-medium ${color}`}>{score}</span>
    },
  }),
  columnHelper.accessor('url', {
    header: 'Profile',
    cell: (info) => (
      <a
        href={info.getValue()}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
      >
        <ExternalLink className="w-3 h-3" />
        View
      </a>
    ),
  }),
]

interface ProfilesTableProps {
  profiles: Profile[]
}

export function ProfilesTable({ profiles }: ProfilesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'intent_match', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data: profiles,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <input
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-md text-sm"
          placeholder="Search profiles..."
        />
        <span className="text-xs text-slate-500">{profiles.length} profiles</span>
      </div>

      <div className="border border-slate-800 rounded-md overflow-hidden">
        <div className="max-w-full overflow-auto max-h-96">
          <table className="min-w-[820px] w-full text-sm">
            <thead className="bg-slate-900 sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-normal cursor-pointer hover:bg-slate-800"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-800">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-900">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
